import { useState, useCallback } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, CheckCircle2 } from "lucide-react";
import { getReviewQueueAll } from "@/services/reviewApi";
import { markProductReviewed, markVariantReviewed } from "@/services/reviewApi";
import type { MarkReviewedRequest } from "@/types/review";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";

interface BulkReviewDialogProps {
  source: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

type Phase = "idle" | "fetching" | "marking" | "done" | "error";

export function BulkReviewDialog({
  source,
  open,
  onOpenChange,
  onSuccess,
}: BulkReviewDialogProps) {
  const { user } = useAuth();
  const reviewerId =
    user?.email ?? user?.name ?? (user ? String(user.id) : "unknown");

  const [notes, setNotes] = useState("");
  const [phase, setPhase] = useState<Phase>("idle");
  const [total, setTotal] = useState(0);
  const [done, setDone] = useState(0);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const runBulk = useCallback(async () => {
    if (!source.trim()) return;
    setPhase("fetching");
    setErrorMessage(null);
    setDone(0);
    setTotal(0);

    try {
      const items = await getReviewQueueAll({ source: source.trim() });
      setTotal(items.length);

      if (items.length === 0) {
        setPhase("done");
        toast.success("No items to review for this source.");
        return;
      }

      setPhase("marking");
      const body: MarkReviewedRequest = {
        reviewer_type: "human",
        reviewer_identifier: reviewerId,
        review_type: "direct",
        ...(notes.trim() && { notes: `Bulk review: ${notes.trim()}` }),
      };

      let completed = 0;
      for (const item of items) {
        try {
          if (item.entity_type === "product") {
            await markProductReviewed(item.product_id, body);
          } else if (item.variant_id != null) {
            await markVariantReviewed(item.variant_id, body);
          }
        } catch (e) {
          console.error("Bulk review item failed", item, e);
          toast.error(
            `Failed at ${completed + 1}/${items.length}: ${e instanceof Error ? e.message : "Unknown error"}`,
          );
          setPhase("error");
          setErrorMessage(e instanceof Error ? e.message : "Unknown error");
          return;
        }
        completed += 1;
        setDone(completed);
      }

      setPhase("done");
      toast.success(
        `Marked ${items.length} items as reviewed for source "${source}".`,
      );
      setNotes("");
      onOpenChange(false);
      onSuccess?.();
    } catch (e) {
      setPhase("error");
      setErrorMessage(e instanceof Error ? e.message : "Unknown error");
      toast.error(e instanceof Error ? e.message : "Failed to load queue");
    }
  }, [source, notes, reviewerId, onSuccess, onOpenChange]);

  const progressPercent = total > 0 ? Math.round((done / total) * 100) : 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Bulk review source: {source}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">
            This will load all items in the queue for this source and mark each
            one as reviewed. Use this after you have already verified the source
            elsewhere.
          </p>

          <div>
            <Label htmlFor="bulk-notes">Notes (optional)</Label>
            <Textarea
              id="bulk-notes"
              className="mt-1"
              placeholder="e.g. Verified via full source scrape"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              disabled={phase !== "idle"}
            />
          </div>

          {(phase === "fetching" || phase === "marking") && (
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">
                  {phase === "fetching"
                    ? "Loading queue…"
                    : `Marking as reviewed ${done}/${total}`}
                </span>
              </div>
              {phase === "marking" && (
                <div className="h-2 w-full rounded-full bg-muted overflow-hidden">
                  <div
                    className="h-full bg-primary transition-all duration-200"
                    style={{ width: `${progressPercent}%` }}
                  />
                </div>
              )}
              {phase === "fetching" && (
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Fetching all items…
                </div>
              )}
            </div>
          )}

          {phase === "done" && (
            <div className="flex items-center gap-2 text-green-600 dark:text-green-400">
              <CheckCircle2 className="w-5 h-5" />
              <span>All {total} items marked as reviewed.</span>
            </div>
          )}

          {phase === "error" && errorMessage && (
            <p className="text-sm text-destructive">{errorMessage}</p>
          )}
        </div>

        <DialogFooter>
          {phase === "idle" && (
            <>
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button onClick={runBulk}>Start bulk review</Button>
            </>
          )}
          {(phase === "done" || phase === "error") && (
            <Button onClick={() => onOpenChange(false)}>Close</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
